import merge from 'lodash/merge';
import { useInViewport } from 'ahooks';
import { SIDE_SEP_LENGTH } from './dataFlow';
import { If } from 'common/src/components/if';
import type { IChartProps } from 'bizcharts/lib/interface';
import type { ILegend } from 'bizcharts/lib/components/Legend';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import Loading from 'common/src/components/loading/loading';
import { ICapabilityProps, ChartColorType } from './interface';
import { breakTextByLimit } from 'common/src/components/text/break';
import { useBizcharts } from '@umu/umu-react-hooks';
import LoadingV2 from 'common/src/components/loadingV2/loading';

const tooltipDefault = {
    shared: true,
};

const selectLabelDefaultConfig = {
    color: '#1e6ee6',
    defaultColor: '#666666',
    isMultiSelect: false,
};

interface IProps extends ICapabilityProps {
    /** 数值轴（纵轴）标题，如单位「人」；默认沿轴居中，避免与刻度重叠 */
    valueAxisTitle?: string;
    /** 合并到 G2 Axis title 配置，可覆盖 position / offset 等 */
    valueAxisTitleProps?: Record<string, unknown>;
    height?: number;
    color?: ChartColorType;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/79#grid
    axisGrid?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/77#scale
    scale?: any;
    // https://bizcharts.taobao.com/product/BizCharts4/category/62/page/80#shared
    tooltip?: any;
    legendProps?: ILegend; // 图例属性
    intervalProps?: IChartProps;
    intervalChartProps?: IChartProps;
    compareChartType?: string;
    customTooltip?: null | ((xVal: string, info: any) => React.ReactNode);
}

const scaleDefault = {
    value: {
        min: 0,
        max: 100,
        tick: 5,
        ticks: [0, 20, 40, 60, 80, 100],
        formatter: (val: number) => {
            return `${val}`;
        },
    },
};
const defaultSliderProps = {
    padding: [20, 20, 0, 0],
    formatter: () => '',
    foregroundStyle: {
        fill: '#1e6ee6', // 网格线的颜色
    },
};
function IntervalChart(props: IProps) {
    const {
        data = [],
        height = 260,
        color = '#2197F3',
        preCls,
        chartProps,
        showInteraction = false,
        initSlider = 1,
        isShowSlider = false,
        hideTooltip = false,
        defaultSelected = [],
        scale = scaleDefault,
        valueAxisTitle,
        valueAxisTitleProps,
        selectLabelConfig,
        isSwitchOnLabelSelect = false,
        tooltip = tooltipDefault,
        customTooltip,
        onLabelSelect,
        formatterLabel,
        getAnnotationProps,
        annotationLines,
        legendProps,
        intervalProps,
        intervalChartProps,
        compareChartType = 'type',
        splitCount = SIDE_SEP_LENGTH,
    } = props;

    const interfaceRef = useRef(null);
    const labelSelectRef = useRef<string[]>([]);
    const [labelSelect, setLabelSelect] = useState<string[]>(defaultSelected);
    const [isShow, setIsShow] = useState(false);
    const [inViewport] = useInViewport(interfaceRef);
    const chartIns = useRef<any>(null);
    const [legendMap, setLegendMap] = useState<Record<string, boolean>>({});

    const labelConfig = merge(selectLabelDefaultConfig, selectLabelConfig);
    const defaultLabelConfig = {
        fontSize: 14,
        lineHeight: 20,
        width: 100,
        wordBreak: 'break-word',
        fill: labelConfig.defaultColor,
    };
    const onChangeSelectLabel = (info: string[]) => {
        setLabelSelect(info);
        onLabelSelect?.(info);
    };
    const onSelectChange = (id: string) => {
        if (!isSwitchOnLabelSelect || !id) {
            return;
        }
        let newLabelList = labelSelectRef.current;
        const isHas = newLabelList.includes(id);
        const isMulti = labelConfig.isMultiSelect;
        if (isHas) {
            newLabelList = isMulti ? newLabelList.filter((item) => item !== id) : [];
        } else {
            newLabelList = isMulti ? [...newLabelList, id] : [id];
        }
        labelSelectRef.current = newLabelList;
        onChangeSelectLabel(newLabelList);
    };
    const onAxisLabelClick = (ev: any) => {
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        onSelectChange(id);
    };
    const onIntervalClick = (ev: any) => {
        const id = ev?.data?.data?.info?.id;
        onSelectChange(id);
    };

    const renderAnnotationMark = () => {
        if (!getAnnotationProps) return null;
        const nodes: React.ReactNode[] = [];
        data.forEach((item: any, index: number) => {
            const markInfo = getAnnotationProps(item);
            const list = Array.isArray(markInfo) ? markInfo : markInfo?.html != null ? [markInfo] : [];
            list.forEach((info: any, subIndex: number) => {
                if (info?.html == null) return;
                const position = info.position ?? [item.label, item.value];
                const rawHtml = info.html;
                const htmlStr =
                    typeof rawHtml === 'string'
                        ? rawHtml
                        : typeof rawHtml === 'function'
                          ? ReactDOMServer.renderToStaticMarkup(
                                React.createElement(React.Fragment, null, (rawHtml as () => React.ReactNode)()),
                            )
                          : '';
                if (!htmlStr) return;
                nodes.push(
                    <Annotation.Html
                        key={`${index}-${subIndex}`}
                        position={position}
                        html={htmlStr}
                        offsetX={info.offsetX}
                        offsetY={info.offsetY}
                        alignX={info.alignX}
                        alignY={info.alignY}
                    />,
                );
            });
        });
        return nodes.length ? nodes : null;
    };

    const renderAnnotationLines = () => {
        if (!annotationLines?.length) return null;
        return annotationLines.map((line, index) => (
            <Annotation.Line
                key={`line-${index}`}
                start={line.start}
                end={line.end}
                style={{
                    stroke: line.style?.stroke ?? '#ad6800',
                    lineDash: line.style?.lineDash ?? [4, 4],
                    lineWidth: line.style?.lineWidth ?? 1,
                }}
            />
        ));
    };

    const onAxisLabelMouseover = (ev: any) => {
        if (!isSwitchOnLabelSelect) {
            return;
        }
        // 修改颜色 - 选中
        ev.target.attr('fill', labelConfig.color);
    };

    const onAxisLabelMouseleave = (ev: any) => {
        if (!isSwitchOnLabelSelect) {
            return;
        }
        const id = ev.target?.cfg?.delegateObject?.item?.id;
        if (labelSelect.includes(id)) {
            return;
        }
        // 修改颜色 - 默认
        ev.target.attr('fill', labelConfig.defaultColor);
    };

    const onLegendChange: any = (ev: { item: any }) => {
        if (!chartIns.current) {
            return;
        }

        const { item } = ev;
        const { value } = item;
        const checked = !item.unchecked;
        legendMap[value] = checked;

        // 通过filter控制元素显示隐藏
        chartIns.current.filter(compareChartType, (val: string) => {
            const isShow = legendMap[val] !== false;
            legendMap[val] = isShow;
            return isShow;
        });
        setLegendMap({ ...legendMap });
        // 重新渲染，触发filter
        chartIns.current.render(true);
    };

    useEffect(() => {
        if (inViewport && !isShow) {
            setIsShow(true);
        }
    }, [inViewport]);

    useEffect(() => {
        onChangeSelectLabel([]);
    }, [data]);

    const BizChartsModules = useBizcharts();

    if (!BizChartsModules) {
        return <LoadingV2 />;
    }

    const { Chart, Axis, Tooltip, Interval, Legend, Slider, registerInteraction, Annotation } = BizChartsModules;

    // 全局注册自定义 active-region 交互定义（仅定义，不绑定到 chart 实例）
    registerInteraction('active-umu-region', {
        start: [{ trigger: 'plot:mousemove', action: 'active-region:show', arg: { style: { fill: '#F5F5F5' } } }],
        end: [{ trigger: 'plot:mouseleave', action: 'active-region:hide' }],
    });

    return (
        <div className={preCls} ref={interfaceRef}>
            <If condition={!isShow}>
                <Loading />
            </If>
            <If condition={isShow}>
                <Chart
                    height={height}
                    data={data}
                    padding={[40, 80, isShowSlider ? 60 : 44, 80]}
                    autoFit
                    theme={{ maxColumnWidth: 40 }}
                    scale={scale}
                    onGetG2Instance={(chart: any) => {
                        chartIns.current = chart;
                        if (showInteraction) {
                            // 在 BizCharts 完成所有交互注册后（下一帧）再绑定 active-umu-region，
                            // 确保它排在 tooltip 交互之后执行，使 #F5F5F5 样式覆盖 tooltip 默认样式。
                            requestAnimationFrame(() => {
                                chart.interaction('active-umu-region');
                            });
                        }
                    }}
                    onIntervalClick={onIntervalClick}
                    onAxisLabelClick={onAxisLabelClick}
                    onAxisLabelMouseover={onAxisLabelMouseover}
                    onAxisLabelMouseleave={onAxisLabelMouseleave}
                    {...chartProps}
                    {...intervalChartProps}
                >
                    <Legend {...legendProps} onChange={onLegendChange} />
                    <Tooltip
                        {...tooltip}
                        visible={!hideTooltip}
                        customContent={
                            customTooltip
                                ? (title: string, items: any[]) => {
                                      const node = customTooltip(title, items);
                                      if (node == null) return '';
                                      return ReactDOMServer.renderToStaticMarkup(node as React.ReactElement);
                                  }
                                : undefined
                        }
                    />

                    <Interval position="label*value" {...intervalProps} color={color} />
                    <Axis
                        name="value"
                        title={
                            valueAxisTitle
                                ? {
                                      text: valueAxisTitle,
                                      // 纵轴：'center' 会使标题落在轴线中段且常伴随旋转；单位放轴端并保持水平
                                      position: 'end',
                                      offset: 18,
                                      autoRotate: false,
                                      style: { fontSize: 12, fill: '#666', fontWeight: 400 },
                                      ...valueAxisTitleProps,
                                  }
                                : undefined
                        }
                        label={{
                            style: defaultLabelConfig,
                        }}
                        grid={{
                            line: {
                                type: 'line', // 网格线类型 line circle polygon
                                style: {
                                    stroke: '#EEEEEE', // 网格线的颜色
                                    lineWidth: 1, // 网格线的宽度复制代码
                                },
                            },
                            alignTick: true,
                        }}
                    />
                    <Axis
                        name="label"
                        line={false}
                        tickLine={false}
                        label={{
                            autoHide: true,
                            autoEllipsis: false,
                            style: isSwitchOnLabelSelect
                                ? (_val, index) => {
                                      const itemInfo = data[index];
                                      return {
                                          ...defaultLabelConfig,
                                          fill: labelSelect.includes(itemInfo.label)
                                              ? labelConfig.color
                                              : labelConfig.defaultColor,
                                      };
                                  }
                                : defaultLabelConfig,
                            formatter(text) {
                                let label = formatterLabel ? formatterLabel(text) : text;
                                label = breakTextByLimit(label, 2, splitCount);
                                return label;
                            },
                        }}
                    />
                    <If condition={isShowSlider}>
                        <Slider start={0} end={initSlider} {...defaultSliderProps} />
                    </If>
                    {renderAnnotationLines()}
                    {renderAnnotationMark()}
                </Chart>
            </If>
        </div>
    );
}

export { IntervalChart };
